import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import debounce from "lodash.debounce";

import { NO_OP_FUNCTION } from "services/helpers";
import "./styles.scss";

class DebouncedInput extends React.Component {
  editor = React.createRef();

  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    const { debounceTime, value, editorRef } = props;

    this.state = {
      text: value,
      focused: false
    };

    this.debouncedUpdateMessage = debounce(
      this.updateMessage,
      debounceTime
    );

    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);

    if (editorRef) {
      this.editor = editorRef;
    }
  }

  /**
   */
  componentDidMount() {
    const { autoFocus, isTextArea } = this.props;

    if (isTextArea) {
      this.updateTextHeight();
    }

    if (this.editor && autoFocus) {
      this.editor.current.focus();
    }
  }


  /**
   * @param {Object} nextProps
   */
  componentWillReceiveProps(nextProps) {
    const { forceValueRefresh } = this.props;
    const { focused, text } = this.state;

    if (
      (!focused || forceValueRefresh) &&
      nextProps.value !== text
    ) {
      this.setState({
        text: nextProps.value
      });
    }
  }

  /**
   */
  componentDidUpdate() {
    const { isTextArea } = this.props;

    if (isTextArea) {
      this.updateTextHeight();
    }
  }

  /**
   */
  onFocus() {
    const { onFocus } = this.props;
    this.setState({ focused: true });
    onFocus();
  }

  /**
   */
  onBlur() {
    const { onBlur } = this.props;
    this.setState({ focused: false });
    onBlur();
  }

  /**
  * @param {Event} e
  */
  static onMouseDown(e) {
    // need this to focus input element inside Draggable element
    e.stopPropagation();
  }

  /**
   * @returns {Object}
   */
  getInputAttributes() {
    const {
      id,
      name,
      type,
      placeholder,
      autoComplete,
      disabled,
      autoCorrect,
      spellCheck,
      tabIndex,
      onKeyPress
    } = this.props;

    const { text } = this.state;

    return {
      id,
      name,
      type: type || "text",
      placeholder,
      value: text || "",
      onChange: this.handleInputChange.bind(this),
      autoComplete,
      disabled,
      autoCorrect,
      spellCheck,
      tabIndex,
      onKeyPress: e => onKeyPress && onKeyPress(e),
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onMouseDown: DebouncedInput.onMouseDown
    };
  }

  /**
   * @param {String} text
   */
  updateMessage(text) {
    const { onChange } = this.props;

    onChange(text);
  }

  /**
   */
  updateTextHeight() {
    const { height: stateHeight } = this.state;

    const height = this.textareaAutosize();

    if (height !== stateHeight) {
      this.setState({
        height
      });
    }
  }

  /**
   * @param {Event} e
   */
  handleInputChange(e) {
    const text = e.target.value;
    this.setState({
      text
    }, () => this.debouncedUpdateMessage(text));
  }

  /**
   * @returns {string|undefined}
   */
  textareaAutosize() {
    const { minHeight } = this.props;

    if (this.editor) {
      const padding = 3;
      const height = this.editor.scrollHeight + padding;

      return `${Math.max(height, minHeight)}px`;
    }

    return undefined;
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      smallFont,
      isInvalid,
      isTextArea,
      isSmall,
      isSmallish,
      customClassName,
      characterCounter
    } = this.props;

    const { text, height } = this.state;

    if (isTextArea) {
      const textLength = text.trim().length;

      return (
        <div className="DebouncedInput__wrapper">

          <textarea
            className={classNames(
              "DebouncedInput",
              "g-input",
              "g-input--type-textarea",
              {
                [customClassName]: customClassName,
                "DebouncedInput--character-counter": characterCounter,
                "DebouncedInput--character-counter--invalid": (textLength > characterCounter),
                "g-input--font-size-small": smallFont,
                "g-input--invalid": isInvalid
              }
            )}
            {...this.getInputAttributes()}
            style={{ height }}
          />

          {characterCounter && (
            <div
              className={classNames(
                "DebouncedInput__character-counter", {
                  "DebouncedInput__character-counter--invalid": (textLength > characterCounter)
                }
              )}
            >
              {characterCounter - textLength}
            </div>
          )}
        </div>
      );
    }

    return (
      <input
        className={classNames(
          "DebouncedInput",
          "g-input", {
            "g-input--invalid": isInvalid,
            "g-input--small": isSmall,
            "g-input--smallish": isSmallish,
            [customClassName]: customClassName
          }
        )}
        ref={this.editor}
        {...this.getInputAttributes()}
      />
    );
  }
}


DebouncedInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  customClassName: PropTypes.string,
  autoFocus: PropTypes.bool,
  /**
   * Used to force value to update even whilst the input is focused
   * e.g. the expression trainer
   */
  forceValueRefresh: PropTypes.bool,
  smallFont: PropTypes.bool,
  isTextArea: PropTypes.bool,
  isSmall: PropTypes.bool,
  minHeight: PropTypes.number,
  debounceTime: PropTypes.number,
  /** Specify max number of characters allowed */
  characterCounter: PropTypes.number,
  isInvalid: PropTypes.bool,
  isSmallish: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  autoComplete: PropTypes.string,
  disabled: PropTypes.bool,
  autoCorrect: PropTypes.string,
  spellCheck: PropTypes.bool,
  tabIndex: PropTypes.number,
  onKeyPress: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  editorRef: PropTypes.exact({ current: PropTypes.node })
};

DebouncedInput.defaultProps = {
  value: null,
  id: null,
  name: null,
  placeholder: null,
  tabIndex: 0,
  type: "text",
  disabled: false,
  spellCheck: true,
  autoComplete: "on",
  autoCorrect: "on",
  customClassName: null,
  characterCounter: null,
  autoFocus: false,
  smallFont: false,
  minHeight: 0,
  debounceTime: 200,
  isInvalid: false,
  isSmallish: false,
  isTextArea: false,
  isSmall: false,
  forceValueRefresh: false,
  onKeyPress: NO_OP_FUNCTION,
  onChange: NO_OP_FUNCTION,
  onFocus: NO_OP_FUNCTION,
  onBlur: NO_OP_FUNCTION,
  editorRef: null
};

export default DebouncedInput;
